import React, { useState, useEffect } from 'react';
import { addObject, subscribeToDDLByDropdownId } from '../utils/storage';
import { ModalTemplate } from './templates';
import SelectExistingObjectModal from './SelectExistingObjectModal';
import './Modal.css';

/**
 * CreateObjectModal
 *
 * Opens on "Select from Existing Objects" (SelectExistingObjectModal) so an
 * object already on file isn't re-created; its "Create New Object" button
 * moves on to the New Object form, which collects only Object Type + Object
 * ID. Everything else (design spec etc.) is filled in later via
 * EditObjectModal.
 *
 * Props:
 *   clientId  – client id the object belongs to
 *   onClose   – called when modal should close without saving
 *   onCreated – called after successful creation
 */
export default function CreateObjectModal({ clientId, onClose, onCreated }) {
  const [step, setStep] = useState('select');   // 'select' | 'new'
  if (step === 'select') {
    return (
      <SelectExistingObjectModal
        clientId={clientId}
        onClose={onClose}
        onCreateNew={() => setStep('new')}
        onCreated={onCreated}
      />
    );
  }
  return <NewObjectForm clientId={clientId} onClose={onClose} onCreated={onCreated} />;
}

function NewObjectForm({ clientId, onClose, onCreated }) {
  const [objectType, setObjectType] = useState('');
  const [objectId,   setObjectId]   = useState('');
  const [saving,     setSaving]     = useState(false);
  const [isDirty,    setIsDirty]    = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  // Live DDL options for Object Type
  const [typeOptions, setTypeOptions] = useState([]);
  const [ddlLoading,  setDdlLoading]  = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToDDLByDropdownId('object_type', (labels) => {
      setTypeOptions(labels);
      setDdlLoading(false);
    });
    return () => unsubscribe();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!objectType || !objectId.trim() || saving) return;
    setSaving(true);
    try {
      await addObject(clientId, objectType, objectId.trim());
      onCreated();
    } catch (err) {
      console.error('Failed to create object:', err);
      setSaveError('Failed to create object. Please check your connection and try again.');
      setSaving(false);
    }
  }

  return (
    <ModalTemplate title="New Object" onClose={onClose} isDirty={isDirty}>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="objType">
            Object Type <span className="required-star">*</span>
          </label>
          {ddlLoading ? (
            <div className="ddl-load-hint">Loading types…</div>
          ) : typeOptions.length > 0 ? (
            <select
              id="objType"
              className="assign-select-full"
              value={objectType}
              onChange={(e) => { setObjectType(e.target.value); setIsDirty(true); setSaveError(null); }}
              required
              autoFocus
            >
              <option value="">— Select a type —</option>
              {typeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <div className="ddl-no-options-hint">
              No types configured. Assign a DDL collection to{' '}
              <strong>Object — Type</strong> in the DDL Collection page first.
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="objTagId">
            Object ID <span className="required-star">*</span>
          </label>
          <input
            id="objTagId"
            type="text"
            value={objectId}
            onChange={(e) => { setObjectId(e.target.value); setIsDirty(true); setSaveError(null); }}
            placeholder="e.g. TK-101, V-203, HX-05"
            required
          />
        </div>

        {saveError && <div className="alert alert-danger" style={{ marginTop: 12 }}>{saveError}</div>}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!objectType || !objectId.trim() || saving}
          >
            {saving ? 'Creating…' : 'Create Object'}
          </button>
        </div>
      </form>
    </ModalTemplate>
  );
}
